import { useEffect, useState } from 'react';

import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import {
  ADD_NEW_IMAGE_TO_EXISTING_COMPONENT,
  ADD_NEW_IMAGE_AND_NEW_COMPONENT,
  ADD_RIVE_FILE_TO_COMPONENT,
  ADD_GIF_FILE_TO_COMPONENT,
} from './component.graphql';
import type {
  AddNewImageAndNewComponentMutation,
  AddNewImageAndNewComponentMutationVariables,
} from './component.graphql.generated';
import type {
  AddRiveFileToComponentMutation,
  AddRiveFileToComponentMutationVariables,
} from './component.graphql.generated';
import type {
  AddGifFileToComponentMutation,
  AddGifFileToComponentMutationVariables,
} from './component.graphql.generated';

export const useAddImageToComponent = (props?: {
  onCompleted?: () => void;
}) => {
  const { showError } = useSnackbar();

  const [addImageToComponent, { loading }] = useLoggingMutation<
    AddNewImageAndNewComponentMutation,
    AddNewImageAndNewComponentMutationVariables
  >(ADD_NEW_IMAGE_TO_EXISTING_COMPONENT, {
    onCompleted: props?.onCompleted,
    onError: () =>
      showError(
        'There was an error adding an image to this component. Please try again.'
      ),
  });

  return {
    addImageToComponent,
    loading,
  };
};

export const useAddImageToNewComponent = (props?: {
  onCompleted?: () => void;
}) => {
  const { showError } = useSnackbar();

  const [addImageToNewComponent, { loading, client }] = useLoggingMutation<
    AddNewImageAndNewComponentMutation,
    AddNewImageAndNewComponentMutationVariables
  >(ADD_NEW_IMAGE_AND_NEW_COMPONENT, {
    onCompleted: props?.onCompleted,
    onError: () =>
      showError(
        'There was an error adding an image to this component. Please try again.'
      ),
  });

  return {
    addImageToNewComponent,
    loading,
    client,
  };
};

export const useFindComponentDimensions = (id: string, selector?: string) => {
  const [elementRef, setRef] = useState<HTMLElement | undefined>(undefined);

  useEffect(() => {
    const el = document.getElementById(
      selector ? `${selector}${id}` : `component-${id}`
    );
    if (el) {
      setRef(el);
    }
  }, [id, selector]);

  return {
    elementRef,
    width: elementRef?.offsetWidth || 0,
    height: elementRef?.offsetHeight || 0,
    elementDimensionLabel: elementRef
      ? `${elementRef.offsetWidth * 2}px x ${
          elementRef.offsetHeight * 2
        }px (W x H)`
      : '...',
  };
};

export const useAddRiveFileToComponent = (props?: {
  onCompleted?: () => void;
}) => {
  const { showError } = useSnackbar();

  const [addRiveFileToComponent, { loading }] = useLoggingMutation<
    AddRiveFileToComponentMutation,
    AddRiveFileToComponentMutationVariables
  >(ADD_RIVE_FILE_TO_COMPONENT, {
    onCompleted: props?.onCompleted,
    onError: () =>
      showError(
        'There was an error adding a Rive file to this component. Please try again.'
      ),
  });

  return {
    addRiveFileToComponent,
    loading,
  };
};

export const useAddGifFileToComponent = (props?: {
  onCompleted?: () => void;
}) => {
  const { showError } = useSnackbar();

  const [addGifFileToComponent, { loading }] = useLoggingMutation<
    AddGifFileToComponentMutation,
    AddGifFileToComponentMutationVariables
  >(ADD_GIF_FILE_TO_COMPONENT, {
    onCompleted: props?.onCompleted,
    onError: () =>
      showError(
        'There was an error adding a Gif file to this component. Please try again.'
      ),
  });

  return {
    addGifFileToComponent,
    loading,
  };
};

export interface InputFieldState<T> {
  editorStateToUpdate: T | undefined;
  setEditorStateToUpdate: React.Dispatch<React.SetStateAction<T | undefined>>;
}

export const useInputFieldState = <T,>({
  onChange,
}: {
  onChange: (data: T) => void;
}): InputFieldState<T> => {
  const [editorStateToUpdate, setEditorStateToUpdate] = useState<
    T | undefined
  >();

  useEffect(() => {
    if (editorStateToUpdate !== undefined) onChange(editorStateToUpdate);

    setEditorStateToUpdate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorStateToUpdate]);

  return {
    editorStateToUpdate,
    setEditorStateToUpdate,
  };
};
